import pako from "pako";
import moment from "moment";

import { useBrandStore } from "@/stores/brand-store";
import { useMessageStore } from "@/stores/message-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useAnnotationStore } from "@/stores/annotation-store";
import { useUserPermissionStore } from "@/stores/user-permission-store";

import type {
  RectangleAnnotation,
  HighlightAnnotation,
  TranscriptAnnotation,
  TimeIndexRedaction,
} from "@/models/Annotation";
import type { Moment } from "moment";
import type { Mark } from "@/models/Mark";
import type { Metadata } from "@/models/Metadata";
import type { MarkBase } from "@/models/MarkBase";
import type { Evidence } from "@/models/Evidence";
import type { SortOrder } from "@/pages/CaseSearch.vue";
import type AgreementPeriod from "@/models/AgreementPeriod";
import type InstanceOfAdvice from "@/models/InstanceOfAdvice";
import type { UserPermissionName } from "@/machines/auth-machine";

export const trimEnd = (target: string, character: string) => {
  return target.replace(new RegExp(`(${character})+$`, "g"), "");
};

export const trimStart = (target: string, character: string) => {
  return target.replace(new RegExp(`^(${character})+`, "g"), "");
};

export const trim = (target: string, character: string) => {
  return trimEnd(trimStart(target, character), character);
};

export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const toMoment = (partialDate: string | string[], lower: boolean): Moment | null => {
  let parts: string[];
  if (typeof partialDate === "string") {
    parts = partialDate.split(" ");
  } else {
    parts = partialDate;
  }

  if (parts.length === 1 && parts[0] === "") {
    return null;
  }

  switch (parts.length) {
    case 1:
      if (lower) {
        return moment(`${parts[0]} 01 01`, "YYYY MM DD");
      }
      return moment(`${parts[0]} 01 01`, "YYYY MM DD").add(1, "year").subtract(1, "day");
    case 2:
      if (lower) {
        return moment(`${parts[0]} ${parts[1]} 01`, "YYYY MM DD");
      }
      return moment(`${parts[0]} ${parts[1]} 01`, "YYYY MM DD").add(1, "month").subtract(1, "day");
    case 3:
      return moment(`${parts[0]} ${parts[1]} ${parts[2]}`, "YYYY MM DD");
    default:
      return null;
  }
};

export const dateInputToUTCDateTime = (date: string, eod = false) => {
  const isoDateRegex = /^(\d{4,6})-(\d{2})-(\d{2})/;

  if (isoDateRegex.test(date)) {
    const [, year, month, day] = isoDateRegex.exec(date)!;

    const result = new Date(Date.UTC(+year, +month - 1, +day));
    if (eod) result.setUTCHours(23, 59, 59, 999);

    return result;
  }

  return new Date(date);
};

export const getDateISOString = (date: Date | undefined) => {
  if (date == null) return "";

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const fromUtc = (
  date: string | number | Date | undefined,
  {
    dateStyle = "medium",
    timeStyle,
  }: Partial<{ [K in "dateStyle" | "timeStyle"]: Intl.DateTimeFormatOptions[K] }> = {},
): string => {
  if (date == null) return "";

  if (typeof date === "number") date = new Date(date);

  const result =
    date instanceof Date
      ? new Date(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          date.getUTCHours(),
          date.getUTCMinutes(),
          date.getUTCSeconds(),
        )
      : dateInputToUTCDateTime(date);

  return result.toLocaleString(undefined, { dateStyle, timeStyle });
};

const ISO_DURATION_REGEX =
  /^P(?:(?<year>\d+)Y)?(?:(?<month>\d+)M)?(?:(?<day>\d+)D)?(?:T((?<hour>\d+)H)?((?<minute>\d+)M)?((?<second>\d+)S)?)?$/;

export const DURATION_PARTS_ORDER = Object.freeze([
  { isTime: false, prop: "year", unit: "Y" },
  { isTime: false, prop: "month", unit: "M" },
  { isTime: false, prop: "day", unit: "D" },
  { isTime: true, prop: "hour", unit: "H" },
  { isTime: true, prop: "minute", unit: "M" },
] as const);
const PRESERVED_DURATION_PARTS = new Set(DURATION_PARTS_ORDER.map((part) => part.prop));

export const parseDuration = (duration: string) => {
  const match = ISO_DURATION_REGEX.exec(duration);

  return Object.entries(match?.groups ?? {}).reduce(
    (acc, [unit, value]) => {
      if (
        PRESERVED_DURATION_PARTS.has(unit as (typeof DURATION_PARTS_ORDER)[number]["prop"]) &&
        value != null &&
        +value > 0
      )
        acc[unit as keyof typeof acc] = +value;
      return acc;
    },
    {} as Partial<Record<(typeof DURATION_PARTS_ORDER)[number]["prop"], number>>,
  );
};

export const addDuration = (date: Date, duration: string) => {
  const parsed = parseDuration(duration);

  const result = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ),
  );

  for (const [_key, value] of Object.entries(parsed)) {
    if (value == null) continue;

    const key = _key as keyof typeof parsed;

    switch (key) {
      case "year":
        result.setUTCFullYear(result.getUTCFullYear() + value);
        break;
      case "month":
        result.setUTCMonth(result.getUTCMonth() + value);
        break;
      case "day":
        result.setUTCDate(result.getUTCDate() + value);
        break;
      case "hour":
        result.setUTCHours(result.getUTCHours() + value);
        break;
      case "minute":
        result.setUTCMinutes(result.getUTCMinutes() + value);
        break;
      default:
        key satisfies never;
    }
  }

  return result;
};

export const formatDuration = (duration: ReturnType<typeof parseDuration>) => {
  return Object.entries(duration)
    .sort(
      ([unitA], [unitB]) =>
        DURATION_PARTS_ORDER.findIndex((o) => o.prop === unitA) -
        DURATION_PARTS_ORDER.findIndex((o) => o.prop === unitB),
    )
    .map(([unit, value]) => `${value} ${unit}${value === 1 ? "" : "s"}`)
    .join(", ");
};

export const toISODuration = (duration: ReturnType<typeof parseDuration>) => {
  const dateParts: string[] = [];
  const timeParts: string[] = [];

  for (const part of DURATION_PARTS_ORDER) {
    const value = duration[part.prop] ?? 0;
    if (value > 0) (part.isTime ? timeParts : dateParts).push(`${value}${part.unit}`);
  }

  if (dateParts.length === 0 && timeParts.length === 0) return null;
  return `P${dateParts.join("")}${timeParts.length > 0 ? `T${timeParts.join("")}` : ""}`;
};

export const regExpEscape = (s: string) => {
  return s.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
};

// Extracted from https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
export const copyToClipboard = (s: string, message = "Selected text copied to clipboard") => {
  const textArea = document.createElement("textarea");

  //
  // *** This styling is an extra step which is likely not required. ***
  //
  // Why is it here? To ensure:
  // 1. the element is able to have focus and selection.
  // 2. if element was to flash render it has minimal visual impact.
  // 3. less flakyness with selection and copying which **might** occur if
  //    the textarea element is not visible.
  //
  // The likelihood is the element won't even render, not even a
  // flash, so some of these are just precautions. However in
  // Internet Explorer the element is visible whilst the popup
  // box asking the user for permission for the web page to
  // copy to the clipboard.
  //

  // Place in top-left corner of screen regardless of scroll position.
  textArea.style.position = "fixed";
  textArea.style.top = "0";
  textArea.style.left = "0";

  // Ensure it has a small width and height. Setting to 1px / 1em
  // doesn't work as this gives a negative w/h on some browsers.
  textArea.style.width = "2em";
  textArea.style.height = "2em";

  // We don't need padding, reducing the size if it does flash render.
  textArea.style.padding = "0";

  // Clean up any borders.
  textArea.style.border = "none";
  textArea.style.outline = "none";
  textArea.style.boxShadow = "none";

  // Avoid flash of white box if rendered for any reason.
  textArea.style.background = "transparent";

  textArea.value = s;
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand("copy");
    if (successful) {
      useMessageStore().updateMessage({
        title: "Text Copied!",
        message,
        type: "info",
        showReload: false,
        autoHide: true,
      });
    }
  } catch (err) {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
};

export const isNumber = (evt: KeyboardEvent, allowSpace = false) => {
  const charCode = evt.which ? evt.which : evt.keyCode;
  if (allowSpace && charCode === 32) return true;
  if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
    evt.preventDefault();
    return false;
  } else {
    return true;
  }
};

export const isThisMe = (
  file: Metadata,
  fileReference: string,
  key: "fileReference" | "parent" = "fileReference",
): boolean => {
  return file[key] === fileReference || (file.aliases ?? []).find((f) => f[key] === fileReference) != null;
};

export function isShowingFile(item: Metadata): boolean {
  const currentFile = useCaseFileStore().currentFile;
  return currentFile?.original === item.original;
}

export const isPasswordProtected = (file: Metadata): boolean => {
  const passwordStatus = file.passwordStatus?.trim() ?? "";
  return passwordStatus !== "" && !/nopassword|success/i.test(passwordStatus);
};

/**
 *
 * @param item The file to check
 * @param checkStore If true, the shared `hasCommittedRedactions` state will be checked to optimistically prepare for the edxBurning flag being toggled
 * @returns
 */
export function isItemBurning(item: Metadata, checkStore = true): boolean {
  return (
    /true/i.test(String(item.edxBurning)) ||
    (checkStore && (useCaseFileStore().cachedFilesData[item.fileReference]?.hasCommittedRedactions ?? false))
  );
}

export function isItemBurned(item: Metadata): boolean {
  return /true/i.test(String(item.edxBurned));
}

export const getGracePeriod = (
  ap: AgreementPeriod,
): AgreementPeriod & Required<Pick<AgreementPeriod, "graceTo" | "graceMonths">> => {
  const FORMAT = "YYYY MM DD";

  if (!ap.graceTo) {
    return {
      ...ap,
      graceTo: ap.to,
      graceMonths: 0,
    };
  }
  const graceTo = moment(ap.graceTo, FORMAT, true);
  const to = moment(ap.to, FORMAT, true);

  return {
    ...ap,
    graceTo: to.isAfter(graceTo) ? ap.to : ap.graceTo,
    graceMonths: graceTo.diff(to, "months", true),
  };
};

export const ioaOverlapsAgreementPeriod = (period: AgreementPeriod, ioa: InstanceOfAdvice): boolean => {
  if (!ioa.startDate || !ioa.endDate) return false;

  const FORMAT = "YYYY MM DD";
  const { from, graceTo } = getGracePeriod(period);

  const periodFrom = moment(from, FORMAT, true);
  const periodTo = moment(graceTo, FORMAT, true);

  const ioaStart = moment(ioa.startDate);
  const ioaEnd = moment(ioa.endDate);

  return (
    (ioaStart.isSameOrBefore(periodFrom) && ioaEnd.isSameOrAfter(periodTo)) ||
    ioaStart.isBetween(periodFrom, periodTo, undefined, "[]") ||
    ioaEnd.isBetween(periodFrom, periodTo, undefined, "[]")
  );
};

export const getMarkStatus = (
  mark?: Pick<Mark, "source" | "evidenceThemes">,
): "user" | "accepted" | "rejected" | "candidate" | "ai" => {
  if (!mark || /user/i.test(mark.source)) return "user";
  if (mark.evidenceThemes?.find((t) => /accepted/i.test(t.status)) != null) return "accepted";
  if (mark.evidenceThemes?.find((t) => /rejected/i.test(t.status)) != null) return "rejected";
  if (mark.evidenceThemes?.find((t) => /bookmark/i.test(t.usage)) != null) return "candidate";
  return "ai";
};

// Compares string case insensitive
export const compare = (a: string, b: string) => {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
};

export const isEqual = (a = "", b = "") => {
  return compare(a, b) === 0;
};

const PERMISSION_NAME_RE = /^[a-z]/;
export function $can(
  permission: UserPermissionName extends infer M
    ? M extends `can${infer P}${infer W}`
      ? `${Lowercase<P>}${W}`
      : never
    : never,
) {
  const fullPermissionString =
    `can${permission.replace(PERMISSION_NAME_RE, (l) => l.toUpperCase())}` as UserPermissionName;
  return useUserPermissionStore().userPermission[fullPermissionString];
}

export const getAnnotationMeta = (
  markData: Pick<RectangleAnnotation, "rectangle" | "pageData"> | Pick<HighlightAnnotation, "lineGroups">,
) => {
  if ("rectangle" in markData) return { rect: markData.rectangle, page: markData.pageData };

  // Mark is text mark, calculate bounding rectangle
  const { lines, pageData } = markData.lineGroups[0];
  const { x, y } = lines.reduce<Record<"x" | "y", number[]>>(
    (acc, cur) => ({ x: [...acc.x, cur.x], y: [...acc.y, cur.y] }),
    { x: [], y: [] },
  );

  return {
    page: pageData,
    rect: {
      x: Math.min(...x),
      y: Math.min(...y),
      width: Math.max(...x) - Math.min(...x),
      height: Math.max(...y) - Math.min(...y),
    },
  };
};

type AllAnnotations = HighlightAnnotation | TranscriptAnnotation | RectangleAnnotation | TimeIndexRedaction;

export function isTranscriptAnnotation(markData: AllAnnotations | undefined): markData is TranscriptAnnotation {
  return (
    markData?.type === "TranscriptAnnotation" ||
    markData?.type === "TranscriptRedaction" ||
    (markData != null && "startTime" in markData && markData.type !== "TimeIndexRedaction")
  );
}

export function isRectangleAnnotation(markData: AllAnnotations | undefined): markData is RectangleAnnotation {
  return (
    markData?.type === "RectangleAnnotation" ||
    markData?.type === "RectangleRedaction" ||
    (markData != null && "method" in markData)
  );
}

export function isHighlightAnnotation(markData: AllAnnotations | undefined): markData is HighlightAnnotation {
  return (
    markData?.type === "HighlightAnnotation" ||
    markData?.type === "LineAnnotation" ||
    markData?.type === "TextSelectionRedaction"
  );
}

export function isTimeIndexRedaction(markData: AllAnnotations | undefined): markData is TimeIndexRedaction {
  return markData?.type === "TimeIndexRedaction";
}

export function getEvidenceDisplayValue(item: Evidence) {
  if (isEqual("annotation", item.type)) {
    const annotation = useAnnotationStore().annotations.find((a) => a.markId === item.value);
    if (!annotation) return 1;

    const markData = annotation.parsedMarkData;

    if (isTimeIndexRedaction(markData) || isTranscriptAnnotation(markData)) {
      return `${toDuration(markData.startTime, true)} - ${toDuration(markData.endTime, true)}`;
    }

    return `Page ${markData?.pageNumber ?? 1}`;
  } else return item.value;
}

export function getMarkEvidences(mark: Pick<MarkBase, "uid">) {
  return useEvidenceStore().evidence.filter((e) => e.value === mark.uid);
}

export function getEvidenceText(item: Evidence) {
  if (item.annotation) return item.annotation;
  return getDefaultEvidenceText(item);
}

export function getDefaultEvidenceText(item: Evidence) {
  const file = useMetadataStore().filesByRef[item.fileReference];
  if (file == null) return;

  return `${file.displayName} \u00B7 (${getEvidenceDisplayValue(item)})`;
}

export async function readBlobAsText(blob: Blob) {
  const arrayBuffer = new Promise<ArrayBufferLike>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function () {
      resolve(reader.result as ArrayBuffer);
    };
    reader.onerror = function (error) {
      reject(error);
    };
    reader.readAsArrayBuffer(blob);
  });

  let data = new Uint8Array(await arrayBuffer);
  // Is this data compressed?
  if (data[0] === 31 && data[1] === 139) {
    data = pako.inflate(data);
  }
  // Convert the decompressed data to a string if it's text
  return new TextDecoder().decode(data);
}

export function toDuration(seconds = 0, min = false) {
  const d = Number(seconds);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);

  const result = (h < 10 ? "0" : "") + h + ":" + ((m < 10 ? "0" : "") + m + ":") + (s < 10 ? "0" : "") + s;

  if (min) return result.replace(/^00:/, "");
  return result;
}

export const toDurationHumanReadableText = (seconds = 0, min = false) => {
  const isPlural =
    (t: string, suffix = "") =>
    (v: number, next = 0) =>
      `${t}${v === 1 ? "" : "s"}${next > 0 ? suffix : ""}`;

  const text = min
    ? { h: () => "h", m: () => "m", s: () => "s" }
    : { h: isPlural(" hour", ","), m: isPlural(" minute", ","), s: isPlural(" second") };

  const d = Number(seconds);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);

  return (
    [h > 0 && `${h}${text.h(h, m)}`, m > 0 && `${m}${text.m(m, s)}`, s > 0 && `${s}${text.s(s)}`]
      .filter(Boolean)
      .join(" ") || `0${text.s(0)}`
  );
};

export const toDurationISO8601Text = (seconds = 0) => {
  const d = Number(seconds);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);

  return `PT${h}H${m}M${s}S`;
};

export const getDefaultCaseSortOrder = (): SortOrder => {
  const nonDefault = useBrandStore().caseSearchConfig?.find((col) => col.defaultSort != null);
  if (nonDefault == null) return { order: "desc", path: "search.score()" };

  return { order: nonDefault.defaultSort ?? "desc", path: nonDefault.fieldPath };
};

export const cleanUtteranceText = (text: string) => text.replaceAll(/<br\s?.*?\/?>/g, "").trim();
